// packages/web-ui/src/panels/chat-panel/ChatPanel.tsx
//
// W2-S5 AC #1 — `<ChatPanel>` shell. Mounts:
//   <SessionHeader>
//   <CapabilityChipRow>
//   <MessageList> (with <ThinkingIndicator> footer)
//   <ReconnectingBanner>
//   composer placeholder (W2-S7)
//
// The component reads everything it needs from the root store via narrow
// selectors, and dispatches via slice actions. Keeps the file declarative
// and testable.
//
// Composes `inlineCardHandlers` and forwards `inlineCardsByMessageId`
// through `<MessageList>` to each `<MessageBubble>`. The handlers wire the
// inline-card kinds:
//   - `kbChangeTreeShell.onOpenDiff` → `useOpenResource('diff', changesetId)`
//   - `responseView.{onFetchLayout,onOpen}` → views slice + Views-tab open
//   - `clarification.onSubmit` → `chat.sendMessage`
//   - `fileUpload` is left undefined for now (W2-S7 composer territory).
// The shell's embedded `<KbChangeTree mode="inline">` receives the root
// store via the handler so it shares state with the Diff tab.
//
// Migration final fix-pass (BLOCKER-1) — `<DemoComposer>` is replaced with
// the real `<Composer>` (W2-S7) now that `@tessa-lean/base` ships ESM-first
// output (`"type": "module"` + `tsc --module NodeNext`). The previous
// playback shim's CJS-resolution warning is no longer relevant. Composer
// host wiring (callbacks `onComposer*`) lives at lines ~240-300; the
// `kbMentionFetcher` factory closure is below.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useRootStore } from '../../state/store.js';
import {
  selectMessages,
  selectStreamingMessageId,
  selectInFlightUserMessageCount,
  selectPendingAssistantMessageId,
  selectPendingStartedAt,
  selectSessionMeta,
} from '../../state/chat.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import { useContextWindowForModel } from '../../hooks/useContextWindowForModel.js';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';
import type { InlineCardHandlers } from '../../components/inline-cards/index.js';
import { useOpenResource } from '../../hooks/useOpenResource.js';
import { getDefaultApiContext } from '../../api/client.js';
import { viewsApi } from '../../api/clients/views.js';
import { filesApi } from '../../api/clients/files.js';
import { defaultViewsWireAdapter } from '../../state/views.js';
import { defaultImageViewsApiAdapter } from '../../state/image-views.js';
import { kbApi } from '../../api/clients/kb.js';
import { Composer } from '../../components/composer/index.js';
import type { MentionCandidateFetcher } from '../../hooks/useMentionAutocomplete.js';
import { SessionHeader } from './SessionHeader.js';
import { CapabilityChipRow } from './CapabilityChipRow.js';
import { computeCapabilityTier } from './compute-capability-tier.js';
import { MessageList } from './MessageList.js';
import { ReconnectingBanner } from './ReconnectingBanner.js';
import { ThinkingIndicator } from './ThinkingIndicator.js';
import { useThinkingIndicatorVisible } from './useThinkingIndicator.js';

const DEFAULT_CAPABILITIES = {
  hasGit: false,
  hasKbConfig: false,
  hasDomainAgent: false,
  activeSkillsCount: 0,
};

export interface ChatPanelProps {
  sessionId: string;
}

export function ChatPanel({ sessionId }: ChatPanelProps): JSX.Element {
  const meta = useRootStore((s) => selectSessionMeta(sessionId)(s));
  const messages = useRootStore((s) => selectMessages(sessionId)(s));
  const streamingMessageId = useRootStore((s) => selectStreamingMessageId(sessionId)(s));
  const pendingAssistantId = useRootStore((s) =>
    selectPendingAssistantMessageId(sessionId)(s),
  );
  const pendingStartedAt = useRootStore((s) => selectPendingStartedAt(sessionId)(s));
  const inFlightCount = useRootStore((s) => selectInFlightUserMessageCount(sessionId)(s));
  const lastTokenAt = useRootStore((s) => s.lastTokenAtBySession[sessionId] ?? null);
  const lastToolCallAt = useRootStore((s) => s.lastToolCallAtBySession[sessionId] ?? null);
  const lastToolResultAt = useRootStore((s) => s.lastToolResultAtBySession[sessionId] ?? null);
  const sseState = useRootStore((s) => s.connectionState);
  const reconnectAttempts = useRootStore((s) => s.reconnectAttempts);
  const beginConnect = useRootStore((s) => s.beginConnect);
  const resetAttempts = useRootStore((s) => s.resetAttempts);

  const setActiveSession = useRootStore((s) => s.setActiveSession);
  const setSessionTitleAction = useRootStore((s) => s.setSessionTitle);
  const setSessionMeta = useRootStore((s) => s.setSessionMeta);
  const regenerateLast = useRootStore((s) => s.regenerateLast);
  const exportTranscript = useRootStore((s) => s.exportTranscript);
  const clearMessages = useRootStore((s) => s.clearMessages);
  const loadTranscript = useRootStore((s) => s.loadTranscript);
  const deleteSessionAction = useRootStore((s) => s.deleteSession);
  const deleteMessage = useRootStore((s) => s.deleteMessage);
  const closeTab = useRootStore((s) => s.closeTab);
  const retryToolCall = useRootStore((s) => s.retryToolCall);
  const rerunToolCall = useRootStore((s) => s.rerunToolCall);

  // Per-message delete confirmation dialog state. `pendingDeleteId` holds
  // the id of the message awaiting confirmation; null when the dialog is
  // closed. Confirming dispatches `deleteMessage(sessionId, id)`.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // W4 fix-pass HIGH-F — inline-card slice + W4 slice readouts. The
  // `inlineCardsByMessageId` map is forwarded to each <MessageBubble>;
  // the slice actions are read here so the handler closures don't have
  // to call `useRootStore.getState()` on every invocation.
  const inlineCardsByMessageId = useRootStore((s) => s.cardsByMessageId);
  const sendMessage = useRootStore((s) => s.sendMessage);
  const openResource = useOpenResource();

  // Mark this session active whenever the panel mounts. Reconciliation with
  // navigation is handled by W2-S4 via `setActiveTab`; here we only inform
  // the chat slice which session is in focus.
  useEffect(() => {
    setActiveSession(sessionId);
  }, [sessionId, setActiveSession]);

  // Rehydrate from server on mount when Zustand has no cached messages.
  // TabContent unmounts inactive tabs, so this fires on every tab return
  // (and on page reload). Guarded on empty so an active conversation is
  // never clobbered.
  useEffect(() => {
    if (messages.length === 0) {
      void loadTranscript(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, loadTranscript]);

  const thinkingVisible = useThinkingIndicatorVisible({
    pendingAssistantMessageId: pendingAssistantId,
    pendingStartedAt,
    lastTokenAt,
    lastToolCallAt,
    lastToolResultAt,
  });

  // Capability data is workspace-wide, not per-session: the chip row reads
  // straight from the polled `/api/v1/health` snapshot via `useCapabilities()`.
  // Falls back to `DEFAULT_CAPABILITIES` until the first poll lands.
  //
  // `tier` is a derivation over the live snapshot, NOT session-meta state —
  // count how many KB augmentations are present on top of `hasKbConfig`:
  //   basic    → no KB config, or KB only
  //   standard → KB + 1-2 of {hasDomainAgents, hasSubAgents, activeSkillCount > 0}
  //   rich     → KB + all three
  const { capabilities: liveCaps, health: liveHealth } = useCapabilities();
  const headerTitle = meta?.title ?? '';
  // Fix #1 — fall back to the live `/health` provider snapshot when the
  // session meta hasn't been populated yet (loadTranscript doesn't carry
  // the provider id; SSE events only stamp it once a `chat:sessionUsage`
  // frame arrives). `health.provider.name` is the Azure deployment name
  // OR the Bedrock model id — the same key the `llm.contextWindows`
  // settings map keys on.
  const providerName =
    meta?.providerName ?? liveHealth?.provider?.name ?? 'unknown';
  const tokensUsed = meta?.tokensUsed ?? null;
  const contextMax = useContextWindowForModel(providerName);
  const metaCaps = meta?.capabilities ?? DEFAULT_CAPABILITIES;

  // Fix #1 — persist the providerName onto SessionMeta once `/health`
  // resolves so the chat-header chip and the context-window denominator
  // remain stable even when the SPA caches the page (the next mount of
  // ChatPanel will see the meta already populated). Skip when the live
  // health snapshot is unavailable (initial poll in flight) or when the
  // meta already carries the same value — avoids a render loop.
  const livenessProviderName = liveHealth?.provider?.name;
  useEffect(() => {
    if (livenessProviderName === undefined) return;
    if (meta?.providerName === livenessProviderName) return;
    setSessionMeta(sessionId, { providerName: livenessProviderName });
  }, [sessionId, livenessProviderName, meta?.providerName, setSessionMeta]);
  const capabilities = useMemo(
    () => ({
      hasGit: liveCaps?.hasGit ?? metaCaps.hasGit,
      hasKbConfig: liveCaps?.hasKbConfig ?? metaCaps.hasKbConfig,
      hasDomainAgent: liveCaps?.hasDomainAgents ?? metaCaps.hasDomainAgent,
      tier: computeCapabilityTier(liveCaps),
      activeSkillsCount: liveCaps?.activeSkillCount ?? metaCaps.activeSkillsCount,
    }),
    [
      liveCaps,
      metaCaps.hasGit,
      metaCaps.hasKbConfig,
      metaCaps.hasDomainAgent,
      metaCaps.activeSkillsCount,
    ],
  );
  const ariaLabel = `Chat session: ${headerTitle.length > 0 ? headerTitle : 'New conversation'}`;
  const isBusy = streamingMessageId !== null || inFlightCount > 0;

  const onExport = useMemo(
    () => (id: string, format: 'json' | 'markdown') => {
      const content = exportTranscript(id, format);
      triggerDownload(`session-${id}.${format === 'json' ? 'json' : 'md'}`, content);
    },
    [exportTranscript],
  );

  const onDelete = useMemo(
    () => (id: string) => {
      void deleteSessionAction(id).then(() => {
        // Close the tab regardless of result so the user isn't stranded on
        // a stale session view.
        closeTab(`chat/${id}`);
      });
    },
    [deleteSessionAction, closeTab],
  );

  const onRequestDeleteMessage = useCallback((messageId: string) => {
    setPendingDeleteId(messageId);
  }, []);

  const onCancelDeleteMessage = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const onConfirmDeleteMessage = useCallback(() => {
    if (pendingDeleteId === null) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    void deleteMessage(sessionId, id);
  }, [deleteMessage, pendingDeleteId, sessionId]);

  const onRegen = useMemo(
    () => (id: string) => {
      void regenerateLast(id);
    },
    [regenerateLast],
  );

  const onSseRetry = useMemo(
    () => () => {
      resetAttempts();
      beginConnect(sessionId);
    },
    [resetAttempts, beginConnect, sessionId],
  );

  // ──── W4 fix-pass HIGH-F — compose inlineCardHandlers ─────────────────
  // The handlers close over the W4 slice actions read above. Each handler
  // is `useCallback`-stable so re-renders don't bust card identity.

  const onFetchLayout = useCallback(
    async (viewId: string): Promise<ReactFlowLayoutJson | null> => {
      // MED-4 polish — centralised ApiContext so bearer-token wiring (W6)
      // doesn't leave this call-site 401-ing while the rest of the SPA works.
      const ctx = getDefaultApiContext();
      const res = await viewsApi.getLayout(ctx, viewId);
      if (!res.ok) return null;
      return res.value;
    },
    [],
  );

  const onOpenView = useCallback(
    (viewId: string): void => {
      openResource('views', viewId);
    },
    [openResource],
  );

  // MAJ-11 — load the raw `view.sysml` for the response-view card's "SysML
  // source" tab. The deterministic Q&A card previously had NO fetcher wired,
  // so the tab always read "unavailable". We resolve the view's manifest (the
  // `/views` summary carries `manifest.sysmlSourcePath`) then read that file
  // via the existing files route (`GET /api/v1/files/content?path=…`) — the
  // SAME mechanism the IllustrativeImagePanel uses. Returns the source text,
  // or null on any failure (the card renders the "unavailable" state). The
  // dead `viewsApi.getSysmlSource` 501-stub client method is removed (AR-5).
  const onFetchSysmlSource = useCallback(
    async (viewId: string): Promise<string | null> => {
      const ctx = getDefaultApiContext();
      const listed = await defaultViewsWireAdapter.listViews(ctx);
      if (!listed.ok) return null;
      const summary = listed.value.find((v) => v.viewId === viewId);
      const sysmlSourcePath = summary?.manifest?.sysmlSourcePath;
      if (typeof sysmlSourcePath !== 'string' || sysmlSourcePath.length === 0) {
        return null;
      }
      const content = await filesApi.getContentReal(ctx, sysmlSourcePath);
      if (!content.ok) return null;
      return content.value.content;
    },
    [],
  );

  const onSaveView = useCallback(
    async (
      viewId: string,
      layout: ReactFlowLayoutJson,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const ctx = getDefaultApiContext();
      const res = await viewsApi.saveLayout(ctx, viewId, layout);
      if (res.ok) return { ok: true };
      return { ok: false, message: res.error.message };
    },
    [],
  );

  const onOpenDiff = useCallback(
    (changesetId: string): void => {
      openResource('diff', changesetId);
    },
    [openResource],
  );

  const onOpenSource = useCallback(
    (filePath: string): void => {
      openResource('files', filePath);
    },
    [openResource],
  );

  const onClarificationSubmit = useCallback(
    (reply: string): void => {
      void sendMessage(sessionId, reply);
    },
    [sendMessage, sessionId],
  );

  // ──── IV3-CARD — IllustrativeImagePanel handler callbacks ─────────────
  // The NL "change the diagram" box POSTs the instruction to the IV2 raster
  // route (202 accept; the produced raster lands later via image:genReady →
  // image-views.resolveReady). The image-views store IS the root store (the
  // slice is composed in `state/store.ts`), so the panel subscribes to live
  // card lifecycle by `imageViewId` directly.
  //
  // MAJ-13 — route Submit THROUGH the image-views slice's `requestEdit`
  // (keyed by `imageViewId`, NOT a raw `viewsApi.requestImageEdit` call).
  // The slice's `beginEdit` engages the optimistic 'generating' skeleton
  // overlay + version lineage BEFORE the POST; the produced image then lands
  // via image:genReady → resolveReady, completing the version history. The
  // prior direct-call path bypassed the slice entirely (no skeleton, no
  // lineage). The panel passes `imageViewId` as the first arg (see
  // IllustrativeImagePanel.doSubmit).
  const onRequestImageEdit = useCallback(
    async (
      imageViewId: string,
      instruction: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      return useRootStore.getState().requestEdit(imageViewId, instruction);
    },
    [],
  );

  // LV3-S1 — opt-in "Generate LLM visualization" follow-up on the deterministic
  // response-view card. POSTs `/views/:viewId/image` with the user's optional
  // free-text layout wishes as the scene-content instruction. The server mints
  // the imageViewId + turnCorrelationId and emits `image:genStarted` (mounts the
  // async skeleton card via SSE) → `image:genReady` / `image:genFailed`. We POST
  // through the shared image-views adapter and return only the 202-accept
  // Result; the sketch card surfaces a non-blocking notice, the raster resolves
  // in its own sibling card with the illustrative badge.
  //
  // `layoutWishes` (the offer's optional input) rides in as `sceneContent`. The
  // server's ImageRenderInvoker routes it to the provider's TRUSTED
  // `styleDirective` channel (obeyed OUTSIDE the injection fence, so a restyle
  // like "make it black and white" takes effect) while the KB scene stays fenced
  // via `promptOverride` so the diagram stays KB-grounded. An EMPTY instruction
  // is intentional for the no-wishes case: the invoker then grounds the raster
  // purely on the persisted KB scene (no promptOverride, no styleDirective),
  // which is the faithful default. So we forward the trimmed wishes and
  // otherwise send '' — never a hard-coded neutral phrase (that phrase would
  // itself become a prompt-override and strip the KB grounding).
  const onGenerateImage = useCallback(
    async (
      viewId: string,
      layoutWishes?: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const ctx = getDefaultApiContext();
      const instruction =
        typeof layoutWishes === 'string' ? layoutWishes.trim() : '';
      const res = await defaultImageViewsApiAdapter.requestImageEdit(
        ctx,
        viewId,
        instruction,
      );
      if (!res.ok) {
        return { ok: false, message: res.error.message };
      }
      return { ok: true };
    },
    [],
  );

  // LV3-S2 — persist the sticky per-view lead engine (Sketch ⇄ LLM image, and
  // the "Back to sketch" reset). Delegates to the `views` slice's
  // `setLeadEngine`, which optimistically patches the matching view's
  // `manifest.leadEngine` then PUTs `/views/:viewId/lead-engine` and rolls back
  // on failure. The slot only renders the switcher when a raster is ready, so a
  // sketch-only view never reaches this.
  const onSetLeadEngine = useCallback(
    async (
      viewId: string,
      leadEngine: 'sketch' | 'llm-image',
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const res = await useRootStore.getState().setLeadEngine(viewId, leadEngine);
      if (res.ok) return { ok: true };
      return { ok: false, message: res.error.message };
    },
    [],
  );

  // Phase 2 — the "Show all" reveal on the scope badge. Delegates to the `views`
  // slice `clearScope`, which PUTs an empty scope to `/views/:viewId/scope` (the
  // server strips it). The card re-fetches the layout on ok, so the badge auto-
  // hides and the full node set renders. Returns only the accept.
  const onClearScope = useCallback(
    async (viewId: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      const res = await useRootStore.getState().clearScope(viewId);
      if (res.ok) return { ok: true };
      return { ok: false, message: res.error.message };
    },
    [],
  );

  // LV3-S3 — the inline "adjust layout" refinement loop. Routes the user's
  // plain-language tweak through the SAME chat send path as a typed message,
  // but stamped with explicit `layoutAdjust` provenance carrying the exact
  // target viewId: the server then routes it straight to the layout compiler
  // for THIS view — no re-judging by the chat layout-vs-question keyword
  // heuristic, and never a fall-through to the KB orchestrator (live-found
  // fix: "move coolant pump up" typed here used to trigger a KB tool storm +
  // an unrequested new view). On `applied` the server re-emits
  // `kb:viewRendered` for the SAME viewId (the card re-renders in place); on an
  // unsupported request the LV2-S3 policy fires the escalated raster sibling.
  // We return only the 202-accept of the send. NOT gated on
  // `views.llmImage.enabled` — the deterministic tweak loop is always-on.
  const onAdjustLayout = useCallback(
    async (
      viewId: string,
      request: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const res = await sendMessage(sessionId, request, undefined, {
        layoutAdjust: { viewId },
      });
      if (res.ok) return { ok: true };
      return { ok: false, message: res.error.message };
    },
    [sendMessage, sessionId],
  );

  // The FOCUSED image/diagram view's own edit channel. Unlike `onAdjustLayout`
  // (which routes through `sendMessage` and therefore lands a turn in the normal
  // chat transcript), this posts the request straight to the view-scoped diagram
  // agent `POST /views/:viewId/edit` — the same endpoint the Views-tab composer
  // uses. The edit is interpreted + executed against THIS view only (content /
  // scope / arrangement) and re-emits `kb:viewRendered`, so the focused view
  // re-renders in place while NOTHING is written to chat. The normal chat is
  // untouched — typing an edit into the main composer still goes through
  // `sendMessage` as before. `outOfScope` (a model change / general question)
  // and an inexpressible request come back as calm inline errors so the user
  // knows to use the chat for those.
  const onEditViewContent = useCallback(
    async (
      viewId: string,
      request: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      const ctx = getDefaultApiContext();
      const res = await viewsApi.editView(ctx, viewId, request);
      if (!res.ok) return { ok: false, message: res.error.message };
      if (res.value.outOfScope === true) {
        return {
          ok: false,
          message:
            'That looks like a model change or a question — ask it in the chat instead.',
        };
      }
      if (res.value.applied === false) {
        return {
          ok: false,
          message:
            'The precise diagram engine couldn’t apply that. Try rephrasing, or use the chat.',
        };
      }
      return { ok: true };
    },
    [],
  );

  // The Diagram-tab "adjust layout" box on the LLM-image card. Unlike the
  // AI-image tab's raster restyle (`onRequestImageEdit`), this changes the
  // DETERMINISTIC diagram via the dedicated `POST /views/:viewId/relayout`
  // endpoint (the SAME one the Views tab's `ViewActionBar` uses). It returns
  // `applied` synchronously so the card can re-fetch its layout in place; on
  // `applied:false` + `escalation:'expressiveness-gap'` the request is beyond
  // the precise renderer and the card nudges the user to the AI image. A
  // dedicated REST call (not the chat-send `onAdjustLayout` above) so the card
  // gets the `applied` verdict without waiting on the SSE re-emit — the
  // illustrative card fetches its Diagram layout locally and has no
  // `layoutRevision` re-key path.
  const onIllustrativeAdjustLayout = useCallback(
    async (
      viewId: string,
      request: string,
    ): Promise<
      | { ok: true; applied: boolean; escalation?: 'expressiveness-gap' }
      | { ok: false; message: string }
    > => {
      const ctx = getDefaultApiContext();
      const res = await viewsApi.relayout(ctx, viewId, request);
      if (!res.ok) return { ok: false, message: res.error.message };
      return {
        ok: true,
        applied: res.value.applied,
        ...(res.value.escalation !== undefined
          ? { escalation: res.value.escalation }
          : {}),
      };
    },
    [],
  );

  // Open the raw `view.sysml` source-of-truth in a SPA tab. Prefer the
  // explicit workspace-relative `sysmlSourcePath` when the card carries one
  // (the manifest pillar); otherwise fall back to opening the view itself.
  const onOpenImageSysmlSource = useCallback(
    (viewId: string, sysmlSourcePath?: string): void => {
      if (typeof sysmlSourcePath === 'string' && sysmlSourcePath.length > 0) {
        openResource('files', sysmlSourcePath);
        return;
      }
      openResource('views', viewId);
    },
    [openResource],
  );

  // Export the rendered raster PNG. The bytes are served at the stable
  // `GET /api/v1/views/:viewId/image` endpoint; trigger a browser download.
  const onExportImagePng = useCallback((viewId: string): void => {
    const ctx = getDefaultApiContext();
    const url = `${ctx.baseUrl}/api/v1/views/${encodeURIComponent(viewId)}/image`;
    triggerNavigate(url);
  }, []);

  // ──── W2 fix-pass §4.1 — Composer host wiring ────────────────────────
  // The full <Composer> (W2-S7) replaces the prior <DemoComposer>. It owns
  // the textarea, mention popup, slash menu, drop zone, ambiguous-mention
  // guard, and Ctrl+Enter / ↑-recall keyboard chords. The host provides:
  //   - onSend: dispatches into chat slice's sendMessage (with chip-built
  //     mentions list).
  //   - onCreateSession, onClearMessages, onOpenHelp, onRegenerateLast:
  //     slash-command dispatch targets.
  //   - onRecallLastUserMessage: returns the last user message body for
  //     the `↑` arrow recall chord; null when no history.
  // `onCancelTurn` is intentionally omitted — there is no chat-slice action
  // to cancel a streaming turn yet (would need to abort the SSE stream and
  // reset `inFlightUserMessageCountBySession`); /cancel slash-command will
  // surface a toast via the composer's dispatchToast prop instead.

  const onComposerSend = useCallback(
    async (payload: {
      body: string;
      mentions?: ReadonlyArray<{ chipText: string; entityId: string }>;
    }): Promise<void> => {
      const mentions = payload.mentions ?? [];
      await sendMessage(sessionId, payload.body, [...mentions]);
    },
    [sendMessage, sessionId],
  );

  const onComposerCreateSession = useCallback(async (): Promise<void> => {
    // Dispatching a `tessa:create-session` CustomEvent lets the (yet to be
    // composed) sessions surface react without forcing the chat panel to
    // hold a sessionsStore reference. The W2-S8 `<SessionList>` listens
    // through its slice's `createSession` action when wiring lands; for
    // now this is a no-op signal.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tessa:create-session'));
    }
  }, []);

  const onComposerClearMessages = useCallback(async (): Promise<void> => {
    await clearMessages(sessionId);
  }, [clearMessages, sessionId]);

  const onComposerOpenHelp = useCallback((): void => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tessa:open-help'));
    }
  }, []);

  const onComposerRegenerateLast = useCallback(async (): Promise<void> => {
    await regenerateLast(sessionId);
  }, [regenerateLast, sessionId]);

  const onComposerRecallLast = useCallback((): string | null => {
    // Walk the active session's messages from the end to find the last
    // user-role message body. Returns null when there is no prior user
    // message (composer's `recallLast()` short-circuits in that case).
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg.role === 'user' && msg.content.length > 0) return msg.content;
    }
    return null;
  }, [messages]);

  // Migration final fix-pass (BLOCKER-1) — `kbMentionFetcher` factory
  // closing over the typed `kbApi.listEntities` client. The Composer's
  // `useMentionAutocomplete` hook calls this with a prefix + AbortSignal
  // and expects a `ReadonlyArray<KbEntity>` back; we forward the response
  // `items[]`. On error (404 KB_ENTITY_NOT_FOUND, 503
  // KB_INDEX_REBUILDING, network) we return `[]` so the popup shows the
  // empty state rather than a thrown promise. MED-4 polish — bearer-token
  // wiring (W6) now flows through `getDefaultApiContext()` so all four
  // ApiContext construction sites in the SPA share one resolution path.
  const kbMentionFetcher = useMemo<MentionCandidateFetcher>(
    () => async (prefix, _signal) => {
      const ctx = getDefaultApiContext();
      const res = await kbApi.listEntities(ctx, { search: prefix, pageSize: 50 });
      if (!res.ok) return [];
      return res.value.items;
    },
    [],
  );

  const inlineCardHandlers = useMemo<InlineCardHandlers>(
    () => ({
      responseView: {
        onFetchLayout,
        onOpen: onOpenView,
        onSave: onSaveView,
        // MAJ-11 — wire the SysML-source fetcher so "View SysML source" shows
        // real `.sysml` on the common deterministic Q&A card (not "unavailable").
        onFetchSysmlSource,
        // LV3-S1 — opt-in raster follow-up. The card only invokes this when the
        // envelope is rasterOffered + llmImageEnabled (so the switch-OFF path
        // never reaches it). Always wiring the handler is safe: gating is the
        // card's job.
        onGenerateImage,
        // LV3-S2 — persist the sticky per-view lead engine (Sketch ⇄ LLM image
        // switcher + "Back to sketch"). The slot only renders the switcher when
        // a raster is ready, so the sketch-only path never invokes this.
        onSetLeadEngine,
        // LV3-S3 — inline "adjust layout" loop routed through the chat send path.
        onAdjustLayout,
        // Focused-view (expand modal) edits use the DIRECT view-scoped diagram
        // agent instead — they execute against this view and never post to chat.
        onEditViewContent,
        // Phase 2 — "Show all" reveal on the scope badge.
        onClearScope,
        // LV3-S2 — the live root store the response-view slot subscribes to (by
        // viewId) to derive the per-view sticky `leadEngine` (views slice) and
        // the raster-ready surface (image-views slice). The `useRootStore`
        // create-hook exposes the StoreApi surface (`getState`/`subscribe`) the
        // slot's `useStore` needs; cast through `unknown` because the root state
        // is a superset of `ViewsSlice & ImageViewsSlice`.
        store: useRootStore as unknown as NonNullable<
          InlineCardHandlers['responseView']
        >['store'],
      },
      kbChangeTreeShell: {
        onOpenDiff,
        // W4 fix-pass HIGH-G — pass the root store so the embedded
        // `<KbChangeTree mode="inline">` shares state with the Diff tab
        // (the same composed slice the DiffTab subscribes to). The
        // `useRootStore` create-hook exposes the StoreApi surface
        // (`getState`/`setState`/`subscribe`) the inner `useStore` call
        // needs; we cast through `unknown` because the root state is a
        // superset of `ChangesetSlice` and TS can't narrow that
        // automatically.
        store: useRootStore as unknown as NonNullable<
          NonNullable<InlineCardHandlers['kbChangeTreeShell']>['store']
        >,
        treeDisabled: false,
      },
      clarification: {
        onSubmit: onClarificationSubmit,
      },
      // IV3-CARD — IllustrativeImagePanel handlers. The image-views store IS
      // the root store (the slice is composed in `state/store.ts`); the panel
      // subscribes by `imageViewId` for the live card lifecycle. The
      // `useRootStore` create-hook exposes the StoreApi surface the panel's
      // `useStore(handlers.store, ...)` needs; we cast through `unknown`
      // because the root state is a superset of `ImageViewsSlice`.
      illustrativeImage: {
        store: useRootStore as unknown as NonNullable<
          InlineCardHandlers['illustrativeImage']
        >['store'],
        onRequestImageEdit,
        onOpenSysmlSource: onOpenImageSysmlSource,
        onExportPng: onExportImagePng,
        // Merged-card wiring — the unified IllustrativeImagePanel now hosts the
        // "Diagram" (deterministic ReactFlow) and "SysML source" tabs too. Reuse
        // the SAME response-view fetchers: the illustrative card's `viewId`
        // equals the response-view `viewId`.
        onFetchLayout,
        onFetchSysmlSource,
        // The Diagram tab's DETERMINISTIC "adjust layout" box — routes through
        // the dedicated relayout endpoint (returns `applied` so the card
        // re-fetches in place). Separate from `onRequestImageEdit`, which
        // restyles the AI-image raster.
        onAdjustLayout: onIllustrativeAdjustLayout,
      },
      // fileUpload is intentionally undefined — the W2-S7 composer story
      // owns that handler bag. <FileUploadConfirm> envelopes that arrive
      // before W2-S7 lands will produce a console.warn from
      // <InlineCardSlot> and render null (defensive).
    }),
    [
      onFetchLayout,
      onOpenView,
      onSaveView,
      onFetchSysmlSource,
      onGenerateImage,
      onSetLeadEngine,
      onAdjustLayout,
      onEditViewContent,
      onClearScope,
      onOpenDiff,
      onClarificationSubmit,
      onRequestImageEdit,
      onOpenImageSysmlSource,
      onExportImagePng,
      onIllustrativeAdjustLayout,
    ],
  );

  return (
    <section
      role="region"
      aria-label={ariaLabel}
      data-testid="chat-panel"
      data-session-id={sessionId}
      className="flex h-full flex-col"
    >
      <SessionHeader
        sessionId={sessionId}
        title={headerTitle}
        providerName={providerName}
        tokensUsed={tokensUsed}
        isBusy={isBusy}
        onRename={setSessionTitleAction}
        onRegenerateLast={onRegen}
        onExport={onExport}
        onDelete={onDelete}
      />
      <CapabilityChipRow capabilities={capabilities} />
      <MessageList
        messages={messages}
        streamingMessageId={streamingMessageId}
        onRetryToolCall={retryToolCall}
        onRerunToolCall={rerunToolCall}
        onOpenSource={onOpenSource}
        inlineCardsByMessageId={inlineCardsByMessageId}
        inlineCardHandlers={inlineCardHandlers}
        onDeleteMessage={onRequestDeleteMessage}
        footer={<ThinkingIndicator visible={thinkingVisible} />}
      />
      {pendingDeleteId !== null ? (
        <DeleteMessageConfirmDialog
          onCancel={onCancelDeleteMessage}
          onConfirm={onConfirmDeleteMessage}
        />
      ) : null}
      <ReconnectingBanner
        state={sseState}
        reconnectAttempts={reconnectAttempts}
        onRetry={onSseRetry}
      />
      <Composer
        sessionId={sessionId}
        providerId={providerName}
        mentionFetcher={kbMentionFetcher}
        onSend={onComposerSend}
        onCreateSession={onComposerCreateSession}
        onClearMessages={onComposerClearMessages}
        onOpenHelp={onComposerOpenHelp}
        onRegenerateLast={onComposerRegenerateLast}
        onRecallLastUserMessage={onComposerRecallLast}
        disabled={isBusy}
        tokensUsed={tokensUsed}
        contextMax={contextMax}
      />
    </section>
  );
}

function DeleteMessageConfirmDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      data-testid="delete-message-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
    >
      <div className="border-taupe-500 bg-cream-50 rounded-none border p-5">
        <h2 className="mb-2 text-sm font-semibold text-taupe-900">Delete this message?</h2>
        <p className="mb-3 text-sm text-taupe-700">This cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="delete-message-dialog-cancel"
            onClick={onCancel}
            className="rounded border border-taupe-500 px-2 py-0.5 text-sm hover:bg-cream-200"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="delete-message-dialog-confirm"
            onClick={onConfirm}
            className="rounded bg-accent-red px-2 py-0.5 text-sm text-white hover:opacity-90"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Browser-only helper that triggers a download of a server URL (the rendered
 * raster PNG endpoint). Uses an anchor with `download` so the browser saves
 * the file rather than navigating away. No-op outside a DOM environment.
 */
function triggerNavigate(url: string): void {
  if (typeof document === 'undefined') return;
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.target = '_blank';
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Browser-only file download helper for transcript export. */
function triggerDownload(filename: string, content: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
